import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  Compass,
  Cpu,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Linkedin,
  MapPin,
  CheckCircle2,
  Layers,
  Award,
  Users,
} from 'lucide-react';

const STEPS_NAV = [
  { id: 1, num: '01', title: 'Origin', tag: 'Pink City to World' },
  { id: 2, num: '02', title: 'The Belief', tag: 'Core Convictions' },
  { id: 3, num: '03', title: 'The InnovHer Difference', tag: 'Distinct Lens' },
  { id: 4, num: '04', title: 'The Journey', tag: 'Visual Timeline' },
  { id: 5, num: '05', title: 'Our Patrons', tag: 'Investors & Backers' },
  { id: 6, num: '06', title: 'Leadership', tag: 'Founders & Leadership' },
];

export default function SixStepsSection() {
  const [activeStep, setActiveStep] = useState(1);
  const containerRef = useRef(null);

  // Jump to step on click
  const goToStep = (stepNumber) => {
    setActiveStep(stepNumber);
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.top < -50 || rect.top > window.innerHeight * 0.4) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const nextStep = () => {
    const next = activeStep < 6 ? activeStep + 1 : 1;
    goToStep(next);
  };

  const prevStep = () => {
    const prev = activeStep > 1 ? activeStep - 1 : 6;
    goToStep(prev);
  };

  return (
    <div
      ref={containerRef}
      id="innovher-evolution-container"
      className="relative bg-[#08080a] text-white border-t border-white/5 py-12 lg:py-20"
    >
      <section
        id="innovher-evolution"
        className="relative flex flex-col justify-center bg-[#08080a] text-white overflow-hidden"
      >
        {/* Ambient pink glowing backlights */}
        <div className="absolute top-1/4 left-1/4 w-[650px] h-[650px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-[550px] h-[550px] rounded-full bg-[#d42b92]/8 blur-[160px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          {/* <div className="max-w-3xl mb-10">
            <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.3em] uppercase mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block animate-pulse" />
              <span>HOW INNOVHER BUILDS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.12]">
              The Journey, Convictions &amp;{' '}
              <span className="text-[#d42b92]">People Behind InnovHer</span>
            </h2>
            <p className="mt-3 text-white/60 text-sm sm:text-base">
              Explore the six defining pillars shaping our venture-building ecosystem.
            </p>
          </div> */}

          {/* Interactive Step Navigator Bar */}
          <div className="mb-12 p-2 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {STEPS_NAV.map((step) => {
                const isActive = activeStep === step.id;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => goToStep(step.id)}
                    className={`text-left p-3.5 rounded-xl border transition-all duration-300 relative group flex flex-col justify-between ${isActive
                      ? 'border-[#d42b92] bg-[#d42b92]/15 shadow-lg shadow-[#d42b92]/20'
                      : 'border-transparent bg-transparent hover:border-white/15 hover:bg-white/[0.03]'
                      }`}
                  >
                    <div>
                      <h4
                        className={`text-xs sm:text-[13px] font-bold transition-colors leading-tight ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                          }`}
                      >
                        {step.title}
                      </h4>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mt-0.5 truncate">
                        {step.tag}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step Content Display Panel */}
          <div className="min-h-[540px] rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] via-[#0d0d12]/90 to-transparent p-6 sm:p-10 lg:p-12 shadow-2xl relative overflow-hidden transition-all duration-300">
            <div key={activeStep} className="animate-[fadeIn_0.4s_ease-out] transition-all">
              {/* STEP 1: ORIGIN */}
              {activeStep === 1 && (
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                  {/* Left Column: Narrative */}
                  <div className="lg:col-span-7 space-y-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                        Origin
                      </span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
                      Born in the Pink City.{' '}
                      <span className="text-[#d42b92]">Built for bigger possibilities.</span>
                    </h3>

                    <div className="space-y-4 text-white/80 text-sm sm:text-base leading-relaxed font-normal">
                      <p>
                        InnovHer began in Jaipur with a simple conviction: where a venture originates should never determine how far it can go.
                      </p>
                      <p>
                        Women-led from the beginning, InnovHer brings a distinctly human perspective to venture building, balancing ambition with empathy, instinct with intelligence, and bold ideas with the discipline required to build enduring enterprises.
                      </p>
                      <p>
                        Operating from an emerging entrepreneurial ecosystem has shaped the way InnovHer thinks. When access is not always immediate, process, systems, technology and networks become the equaliser, enabling ventures beyond major metros to build with the sophistication, intelligence and opportunity available in the world’s advanced startup ecosystems.
                      </p>
                      <p>
                        Started by a woman, but strengthened by founders, investors, mentors, businesses and collaborators across the ecosystem, InnovHer continues to build its presence nationally while earning global recognition.
                      </p>
                    </div>

                    <div className="p-4 sm:p-5 rounded-2xl border border-white/10 bg-gradient-to-r from-[#d42b92]/20 via-white/[0.02] to-transparent">
                      <p className="text-base sm:text-lg font-bold text-white">
                        Rooted in Jaipur. Connected across India.{' '}
                        <span className="text-[#ff8ec5]">Building without boundaries.</span>
                      </p>
                    </div>
                  </div>

                  {/* Right Column: Visual Composition (No blank space) */}
                  <div className="lg:col-span-5 space-y-4">
                    <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl aspect-[3/2] group">
                      <img
                        src="/new_images/innovher-team.webp"
                        alt="InnovHer Team"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 filter grayscale contrast-105 group-hover:grayscale-0"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

                      {/* <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs text-white/90 bg-black/60 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10">
                        <div className="flex items-center gap-2 font-medium">
                          <MapPin className="w-4 h-4 text-[#ff8ec5]" />
                          <span>Jaipur, Pink City · Headquarters</span>
                        </div>
                        <span className="text-[#ff8ec5] text-[11px] uppercase tracking-wider font-semibold">Tier-2 to Global</span>
                      </div> */}
                    </div>

                    {/* 3 Metric Pills */}
                    {/* <div className="grid grid-cols-3 gap-3">
                      <div className="p-3.5 rounded-xl border border-white/10 bg-white/[0.02] text-center">
                        <div className="text-xs font-bold text-[#ff8ec5] uppercase tracking-wider">Foundation</div>
                        <div className="text-sm font-semibold text-white mt-0.5">Women-Led</div>
                      </div>
                      <div className="p-3.5 rounded-xl border border-white/10 bg-white/[0.02] text-center">
                        <div className="text-xs font-bold text-[#ff8ec5] uppercase tracking-wider">Method</div>
                        <div className="text-sm font-semibold text-white mt-0.5">Equaliser</div>
                      </div>
                      <div className="p-3.5 rounded-xl border border-white/10 bg-white/[0.02] text-center">
                        <div className="text-xs font-bold text-[#ff8ec5] uppercase tracking-wider">Scale</div>
                        <div className="text-sm font-semibold text-white mt-0.5">National</div>
                      </div>
                    </div> */}
                  </div>
                </div>
              )}

              {/* STEP 2: THE BELIEF */}
              {activeStep === 2 && (
                <div className="space-y-8">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                        The Belief
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                      Core convictions that guide
                    </h3>
                    <h3 className="text-[#d42b92]text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight">how we build.</h3>
                  </div>

                  {/* 5 Beliefs Grid (Full Width, Zero blank space) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                      {
                        num: '01',
                        title: 'Ambition is not geographical.',
                        desc: 'Vision and grit exist everywhere. Location should never cap the potential of what can be built.',
                      },
                      {
                        num: '02',
                        title: 'Capability can be built.',
                        desc: 'With structured venture frameworks, operating discipline, and specialized access, early founders bridge execution gaps rapidly.',
                      },
                      {
                        num: '03',
                        title: 'Access can be engineered.',
                        desc: 'Proximity is not destiny. Networks, capital pathways, and institutional market bridges can be systematically architected.',
                      },
                      {
                        num: '04',
                        title: 'Systems can create an advantage.',
                        desc: 'Repeatable playbooks, institutional memory, and intelligence infrastructures beat one-off heroics every time.',
                      },
                      {
                        num: '05',
                        title: 'Ecosystems can be built.',
                        desc: 'When the right people, capital, knowledge, technology and networks come together, geography becomes less of a constraint.',
                      },
                      {
                        num: '06',
                        title: 'And great ventures can emerge from anywhere.',
                        desc: 'The defining companies of India’s next decade will be built from Tier-2 cities, regional hubs, and diverse founding voices.',
                        highlight: true,
                      },
                    ].map((b) => (
                      <div
                        key={b.num}
                        className={`rounded-2xl border p-6 flex flex-col justify-between transition-all ${b.highlight
                          ? 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                          : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                          }`}
                      >
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            {/* <span
                              className={`text-xs font-mono font-bold ${b.highlight ? 'text-[#ff8ec5]' : 'text-white/40'
                                }`}
                            >
                              BELIEF
                            </span>
                            <CheckCircle2
                              className={`w-4 h-4 ${b.highlight ? 'text-[#ff8ec5]' : 'text-white/30'}`}
                            /> */}
                          </div>
                          <h4 className="text-lg sm:text-xl font-bold text-white leading-snug">
                            {b.title}
                          </h4>
                        </div>
                        <p className="mt-4 text-xs sm:text-sm text-white/70 leading-relaxed font-normal">
                          {b.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 3: THE INNOVHER DIFFERENCE */}
              {activeStep === 3 && (
                <div className="space-y-8">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                        The InnovHer Difference
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                      A distinct lens on <span className="text-[#d42b92]">venture building.</span>
                    </h3>
                  </div>

                  {/* 3 Pillars Grid (Zero blank space) */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Difference 1 */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all flex flex-col justify-between shadow-lg group">
                      <div>
                        <div className="w-12 h-12 rounded-xl bg-[#d42b92]/20 border border-[#d42b92]/40 flex items-center justify-center text-[#ff8ec5] mb-5">
                          {/* <Sparkles className="w-6 h-6" /> */}
                        </div>
                        <h4 className="text-xl font-bold text-white leading-snug">
                          Women-led.{' '}
                          <span className="text-[#ff8ec5]">Ecosystem-powered.</span>
                        </h4>
                        <p className="mt-4 text-xs sm:text-sm text-white/75 leading-relaxed font-normal">
                          Not women-only; rather, a venture-building perspective shaped by empathy, resilience, collaboration and a different lens on opportunity.
                        </p>
                      </div>
                      {/* <div className="mt-8 pt-4 border-t border-white/10 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                        Perspective &amp; Empathy
                      </div> */}
                    </div>

                    {/* Difference 2 */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all flex flex-col justify-between shadow-lg group">
                      <div>
                        <div className="w-12 h-12 rounded-xl bg-[#d42b92]/20 border border-[#d42b92]/40 flex items-center justify-center text-[#ff8ec5] mb-5">
                          <Compass className="w-6 h-6" />
                        </div>
                        <h4 className="text-xl font-bold text-white leading-snug">
                          Indian Business{' '}
                          <span className="text-[#ff8ec5]">Acumen</span>
                        </h4>
                        <p className="mt-4 text-xs sm:text-sm text-white/75 leading-relaxed font-normal">
                          Built with an understanding of how India actually builds businesses. Relationships, capital efficiency, adaptability, market complexity and long-term enterprise thinking.
                        </p>
                      </div>
                      {/* <div className="mt-8 pt-4 border-t border-white/10 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                        Real-World Execution
                      </div> */}
                    </div>

                    {/* Difference 3 */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all flex flex-col justify-between shadow-lg group">
                      <div>
                        <div className="w-12 h-12 rounded-xl bg-[#d42b92]/20 border border-[#d42b92]/40 flex items-center justify-center text-[#ff8ec5] mb-5">
                          <Cpu className="w-6 h-6" />
                        </div>
                        <h4 className="text-xl font-bold text-white leading-snug">
                          Systems Over{' '}
                          <span className="text-[#ff8ec5]">Geography</span>
                        </h4>
                        {/* <div className="mt-2.5 px-2.5 py-1 rounded bg-white/[0.04] border border-white/10 text-[11px] font-mono text-[#ff8ec5] font-semibold inline-block">
                          Process + Technology + Intelligence + Networks
                        </div> */}
                        <p className="mt-3.5 text-xs sm:text-sm text-white/75 leading-relaxed font-normal">
                          Building institutional capability that reduces the disadvantages traditionally associated with operating away from major startup hubs.
                        </p>
                      </div>
                      {/* <div className="mt-8 pt-4 border-t border-white/10 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                        Institutional Capability
                      </div> */}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: THE JOURNEY (VISUAL TIMELINE) */}
              {activeStep === 4 && (
                <div className="space-y-8">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                        The Journey
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                      A visual timeline <span className="text-[#d42b92]">of building.</span>
                    </h3>
                    {/* <p className="mt-2 text-white/60 text-xs sm:text-sm">
                      This gives visitors a sense that InnovHer is still young but evolving quickly.
                    </p> */}
                  </div>

                  {/* Timeline Cards (Zero blank space) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                    {[
                      {
                        step: '01',
                        year: '2024',
                        title: 'InnovHer begins',
                        desc: 'Inception in Jaipur with conviction around decentralized venture building.',
                      },
                      {
                        step: '02',
                        year: '2024',
                        title: 'First accelerator cohort & investments',
                        desc: 'InnovAce flagship cohort deployed and early venture capital catalyzed.',
                      },
                      {
                        step: '03',
                        year: '2025',
                        title: 'Portfolio & ecosystem expansion',
                        desc: 'Scaling active founders, national mentor network, and strategic partners.',
                      },
                      {
                        step: '04',
                        year: '2026',
                        title: 'Venture Studio, Studio I, InnovPulse',
                        desc: 'Physical Jaipur anchor & AI venture intelligence infrastructure operational.',
                      },
                      {
                        step: '05',
                        year: 'Today',
                        title: '32 months of building',
                        desc: 'Compounding venture capabilities, deep relationships, and active startups.',
                        highlight: true,
                      },
                      {
                        step: '06',
                        year: 'Next',
                        title: 'India → developing economies',
                        desc: 'Exporting our decentralized venture studio model to global emerging markets.',
                        future: true,
                      },
                    ].map((item) => (
                      <div
                        key={item.step}
                        className={`rounded-2xl border p-5 flex flex-col justify-between transition-all duration-300 ${item.highlight
                          ? 'border-[#d42b92] bg-[#d42b92]/20 shadow-xl shadow-[#d42b92]/15 scale-[1.02]'
                          : item.future
                            ? 'border-[#ff8ec5]/40 bg-white/[0.04]'
                            : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                          }`}
                      >
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span
                              className={`text-xs font-mono font-bold px-2.5 py-1 rounded-full ${item.highlight
                                ? 'bg-[#d42b92] text-white'
                                : item.future
                                  ? 'bg-[#ff8ec5]/20 text-[#ff8ec5]'
                                  : 'bg-white/10 text-white/70'
                                }`}
                            >
                              {item.year}
                            </span>
                            {/* <span className="text-[11px] text-white/30 font-mono">{item.step}</span> */}
                          </div>
                          <h4 className="text-sm sm:text-base font-bold text-white leading-snug">
                            {item.title}
                          </h4>
                        </div>
                        <p className="mt-3 text-xs text-white/65 leading-relaxed font-normal">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 5: OUR PATRONS */}
              {activeStep === 5 && (
                <div className="space-y-8">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                        Our Patrons
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                      Investors</h3>
                  </div>

                  {/* 4 Patrons Grid (Zero blank space) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      {
                        name: 'Mahavir Sharma',
                        role: 'Chairman, TiE Global Board of Trustees · Angel Investor',
                        image: '/team/madnar-main.webp',
                        profile: 'Veteran angel investor and institutional mentor connecting regional ventures to national and global investment syndicates.',
                        linkedin: 'https://www.linkedin.com/in/mahavirsharma/',
                      },
                      {
                        name: 'Amit Singhal',
                        role: 'Founding Partner, Fluid Ventures · Strategic Investor',
                        image: '/team/Amit-main.webp',
                        profile: 'Venture capitalist backing early-stage consumer and tech ventures with seed capital, governance frameworks, and strategic scale.',
                        linkedin: 'https://www.linkedin.com/in/amit-singhal-ca/',
                      },
                      {
                        name: 'Onkar Bagaria',
                        role: 'CEO, Vivekanand Global University · Institutional Partner',
                        image: '/team/omkarsir.jpeg',
                        profile: 'Thought leader championing youth innovation, university incubation corridors, and regional entrepreneurial education.',
                        linkedin: 'https://www.linkedin.com/in/onkar-bagaria/',
                      },
                      {
                        name: 'Vijetha Shastry',
                        role: 'Strategic Advisor & Ecosystem Leader',
                        image: '/team/vijetha.webp',
                        profile: 'Venture ecosystem builder and seasoned startup strategist connecting founders with institutional networks and open innovation pathways.',
                        linkedin: 'https://www.linkedin.com/in/vijethashastry/',
                      },
                    ].map((patron) => (
                      <div
                        key={patron.name}
                        className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all flex flex-col justify-between shadow-lg"
                      >
                        <div>
                          <div className="w-full aspect-square rounded-xl overflow-hidden bg-white/[0.05] border border-white/10 mb-4">
                            <img
                              src={patron.image}
                              alt={patron.name}
                              className="w-full h-full object-cover object-top filter grayscale contrast-105 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                              loading="lazy"
                            />
                          </div>
                          <h4 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors leading-snug">
                            {patron.name}
                          </h4>
                          <p className="mt-1 text-xs font-semibold text-[#ff8ec5] leading-snug">
                            {patron.role}
                          </p>
                          <p className="mt-3 text-xs text-white/70 leading-relaxed font-normal">
                            {patron.profile}
                          </p>
                        </div>
                        {patron.linkedin && (
                          <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-white/50">
                            <span className="text-[11px] uppercase tracking-wider text-white/40">Patron</span>
                            <a
                              href={patron.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-[#0077b5] transition-colors"
                              aria-label={`View ${patron.name} on LinkedIn`}
                            >
                              <Linkedin className="w-4 h-4" />
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 6: LEADERSHIP */}
              {activeStep === 6 && (
                <div className="space-y-8">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                        Leadership
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                      THE FOUNDER <span className="text-[#d42b92]">BEHIND THE IDEA</span>
                    </h3>
                  </div>

                  {/* Founder Showcase Card (SC) */}
                  <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6 sm:p-8 lg:p-10 shadow-xl">
                    <div className="grid lg:grid-cols-12 gap-8 items-center">
                      <div className="lg:col-span-4">
                        <div className="w-full aspect-[4/4.5] rounded-2xl overflow-hidden border border-white/10 bg-white/[0.05] shadow-2xl">
                          <img
                            src="/shweta22.webp"
                            alt="Dr. Shweta Choudhary"
                            className="w-full h-full object-cover object-top filter grayscale contrast-105 hover:grayscale-0 transition-all duration-500"
                          />
                        </div>
                      </div>
                      <div className="lg:col-span-8">
                        {/* <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs uppercase tracking-widest font-mono px-2.5 py-1 rounded-md bg-[#d42b92]/20 border border-[#d42b92]/40 text-[#ff8ec5] font-bold">
                            SC
                          </span>
                          <span className="text-xs uppercase tracking-wider text-white/50 font-semibold">
                            The Founder Behind The Idea
                          </span>
                        </div> */}
                        <h4 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                          Dr. Shweta Choudhary
                        </h4>
                        <p className="mt-1 text-sm font-semibold text-[#ff8ec5]">
                          Founder &amp; Director, InnovHer
                        </p>
                        <div className="mt-4 space-y-3 text-white/80 text-sm sm:text-base leading-relaxed font-normal">
                          <p>
                            Vision-bearer of InnovHer. Building an institution for venture building where founders meet capability and capital meets readiness.
                          </p>
                          <p>
                            With deep commitment to inclusive entrepreneurship, Dr. Shweta has pioneered structured venture acceleration from Jaipur, transforming ambitious grassroots ideas into scalable, investable enterprises.
                          </p>
                        </div>
                        <div className="mt-6 flex items-center gap-4">
                          <a
                            href="https://www.linkedin.com/in/dr-shweta-choudhary/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#d42b92] hover:bg-[#e0339f] text-white text-xs sm:text-sm font-semibold tracking-wide transition-colors shadow-lg shadow-[#d42b92]/20"
                          >
                            <Linkedin className="w-4 h-4" />
                            <span>Connect on LinkedIn</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Co-Founders: JV & SS */}
                  <div>
                    {/* <h4 className="text-lg font-bold text-white mb-4">Co-Founders</h4> */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* JV */}
                      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all flex flex-col sm:flex-row gap-5 items-center shadow-lg group">
                        <div className="w-28 h-28 rounded-2xl overflow-hidden border border-white/10 shrink-0 bg-white/[0.05]">
                          <img
                            src="/team/ajeet.jpeg"
                            alt="Jeet Vijay"
                            className="w-full h-full object-cover object-top filter grayscale contrast-105 group-hover:grayscale-0 transition-all duration-500"
                          />
                        </div>
                        <div className="flex-1">
                          {/* <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs uppercase tracking-widest font-mono px-2 py-0.5 rounded bg-[#d42b92]/20 border border-[#d42b92]/40 text-[#ff8ec5] font-bold">
                              JV
                            </span>
                            <span className="text-xs text-white/50 uppercase tracking-wider font-semibold">Co-Founder</span>
                          </div> */}
                          <h5 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors">
                            Jeet Vijay
                          </h5>
                          <p className="text-xs font-semibold text-[#ff8ec5]">
                            Lead : Bharat Ventures
                          </p>
                          <p className="mt-2 text-xs text-white/70 leading-relaxed font-normal">
                            Former CEO of MeitY Startup Hub; national venture ecosystem leader catalyzing high-impact startups and grassroots innovation.
                          </p>
                          <div className="mt-3">
                            <a
                              href="https://www.linkedin.com/in/jeetvijay/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 hover:text-[#0077b5] transition-colors"
                            >
                              <Linkedin className="w-3.5 h-3.5" />
                              <span>LinkedIn Profile</span>
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* SS */}
                      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all flex flex-col sm:flex-row gap-5 items-center shadow-lg group">
                        <div className="w-28 h-28 rounded-2xl overflow-hidden border border-white/10 shrink-0 bg-white/[0.05]">
                          <img
                            src="/shivam.webp"
                            alt="Shivam Sharma"
                            className="w-full h-full object-cover object-top filter grayscale contrast-105 group-hover:grayscale-0 transition-all duration-500"
                          />
                        </div>
                        <div className="flex-1">
                          {/* <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs uppercase tracking-widest font-mono px-2 py-0.5 rounded bg-[#d42b92]/20 border border-[#d42b92]/40 text-[#ff8ec5] font-bold">
                              SS
                            </span>
                            <span className="text-xs text-white/50 uppercase tracking-wider font-semibold">Co-Founder</span>
                          </div> */}
                          <h5 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors">
                            Shivam Sharma
                          </h5>
                          <p className="text-xs font-semibold text-[#ff8ec5]">
                            Lead : Bharat XLR8AR
                          </p>
                          <p className="mt-2 text-xs text-white/70 leading-relaxed font-normal">
                            Venture acceleration lead turning early traction into scalable business playbooks and investor-ready commercial models.
                          </p>
                          <div className="mt-3">
                            <a
                              href="https://www.linkedin.com/in/shivam-sharma-6832621a9/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 hover:text-[#0077b5] transition-colors"
                            >
                              <Linkedin className="w-3.5 h-3.5" />
                              <span>LinkedIn Profile</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Stepper Footer Controls */}
            <div className="mt-12 pt-6 border-t border-white/10 flex items-center justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-xs font-semibold transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="hidden sm:flex items-center gap-2">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goToStep(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${activeStep === i ? 'w-8 bg-[#d42b92]' : 'w-2 bg-white/20 hover:bg-white/40'
                      }`}
                    aria-label={`Go to step ${i}`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={nextStep}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#e0339f] text-white text-xs font-semibold tracking-wide transition-all shadow-md shadow-[#d42b92]/20"
              >
                {/* <span>{activeStep === 6 ? 'Back to Origin' : 'Next Step'}</span> */}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
